import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import useLang from '../hooks/useLang';
import useAuthStore from '../stores/authStore';

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-800 mb-5 pb-3 border-b border-gray-100">{title}</h2>
      {children}
    </div>
  );
}

const LEADER_ROLES = [
  { key: 'mwenyekiti', label: 'Mwenyekiti', icon: '👑' },
  { key: 'katibu',     label: 'Katibu',     icon: '📝' },
  { key: 'mwekahazina',label: 'Mwekahazina',icon: '💰' },
];

const PERMISSION_ITEMS = [
  { key: 'transactions',   labelSw: 'Michango',            labelEn: 'Contributions', icon: '💳' },
  { key: 'fines',          labelSw: 'Faini',               labelEn: 'Fines',         icon: '⚠️' },
  { key: 'changiwa',       labelSw: 'Changiwa',            labelEn: 'Changiwa',      icon: '✨' },
  { key: 'monthly_report', labelSw: 'Ripoti ya Kila Mwezi',labelEn: 'Monthly Report',icon: '📅' },
  { key: 'distribution',   labelSw: 'Mgawanyo',            labelEn: 'Distribution',  icon: '📊' },
];

export default function GroupSettings() {
  const { T } = useLang();
  const { id } = useParams();
  const { user } = useAuthStore();
  const isSuperadmin = user?.role === 'superadmin';
  const [group, setGroup] = useState(null);
  const [fineRule, setFineRule] = useState(null);
  const [changiwaTypes, setChangiwaTypes] = useState([]);
  const [transferTypes, setTransferTypes] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({ cycle: false, fine: false, leaders: false, sms: false });
  const [msg, setMsg] = useState({ cycle: '', fine: '', leaders: '', sms: '' });
  const [toast, setToast] = useState(null); // { text, ok }

  const showToast = useCallback((text, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast(null), 3500);
  }, []);
  const [newChangiwa, setNewChangiwa] = useState({ name: '', amount: '', description: '' });
  const [addingChangiwa, setAddingChangiwa] = useState(false);
  const [newTransferType, setNewTransferType] = useState({ name: '', amount: '', description: '' });
  const [addingTransferType, setAddingTransferType] = useState(false);
  const [memberPerms, setMemberPerms] = useState({ transactions: true, fines: true, changiwa: true, monthly_report: true, distribution: true });
  const [savingPerms, setSavingPerms] = useState(false);

  // Leaders: { mwenyekiti: memberId|'', katibu: memberId|'', mwekahazina: memberId|'' }
  const [leaders, setLeaders] = useState({ mwenyekiti: '', katibu: '', mwekahazina: '' });

  const [cycle, setCycle] = useState({
    start_date: '', contributions_from: '', end_date: '', monthly_contribution: '', entry_fee: '',
    opening_balance: 0, dissolve_at_end: false, reserve_months: 0,
  });

  const [fine, setFine] = useState({
    due_day: 5, grace_days: 0, fine_type: 'fixed',
    fine_amount: 0, fine_percentage: 0, is_auto: false,
  });

  useEffect(() => {
    const permPromise = isSuperadmin ? api.get(`/groups/${id}/member-permissions`) : Promise.resolve({ data: null });
    Promise.all([
      api.get(`/groups/${id}`),
      api.get(`/groups/${id}/fine-rules`),
      api.get(`/groups/${id}/changiwa-types`),
      api.get(`/groups/${id}/transfer-types`),
      api.get(`/members?group_id=${id}`),
      permPromise,
    ]).then(([g, fr, ct, tt, mem, pr]) => {
      if (pr.data) setMemberPerms(pr.data);
      setChangiwaTypes(ct.data || []);
      setTransferTypes(tt.data || []);
      setMembers(mem.data || []);
      const grp = g.data;
      setGroup(grp);
      setCycle({
        start_date: grp.start_date || '',
        contributions_from: grp.contributions_from || '',
        end_date: grp.end_date || '',
        monthly_contribution: grp.monthly_contribution || '',
        entry_fee: grp.entry_fee || '',
        opening_balance: grp.opening_balance || 0,
        dissolve_at_end: !!+grp.dissolve_at_end,
        reserve_months: grp.reserve_months || 0,
      });
      if (fr.data && fr.data.id) {
        setFineRule(fr.data);
        setFine({
          due_day: fr.data.due_day || 5,
          grace_days: fr.data.grace_days || 0,
          fine_type: fr.data.fine_type || 'fixed',
          fine_amount: fr.data.fine_amount || 0,
          fine_percentage: fr.data.fine_percentage || 0,
          is_auto: !!+fr.data.is_auto,
        });
      }
      // Pre-populate leader dropdowns from existing roles
      const leaderMap = { mwenyekiti: '', katibu: '', mwekahazina: '' };
      (mem.data || []).forEach(m => {
        if (leaderMap.hasOwnProperty(m.role)) leaderMap[m.role] = String(m.id);
      });
      setLeaders(leaderMap);
    }).finally(() => setLoading(false));
  }, [id]);

  const saveCycle = async (e) => {
    e.preventDefault();
    setSaving(s => ({ ...s, cycle: true }));
    try {
      await api.put(`/groups/${id}/cycle`, cycle);
      showToast(T.settings.toastSavedCycle);
    } catch {
      showToast(T.settings.toastErr, false);
    } finally {
      setSaving(s => ({ ...s, cycle: false }));
    }
  };

  const savePerms = async () => {
    setSavingPerms(true);
    try {
      await api.put(`/groups/${id}/member-permissions`, memberPerms);
      showToast(T.settings.toastSavedPerms || 'Ruhusa zimehifadhiwa');
    } catch {
      showToast(T.settings.toastErr, false);
    } finally {
      setSavingPerms(false);
    }
  };

  const saveFine = async (e) => {
    e.preventDefault();
    setSaving(s => ({ ...s, fine: true }));
    try {
      await api.post(`/groups/${id}/fine-rules`, fine);
      showToast(T.settings.toastSavedFine);
    } catch {
      showToast(T.settings.toastErr, false);
    } finally {
      setSaving(s => ({ ...s, fine: false }));
    }
  };

  const saveLeaders = async (e) => {
    e.preventDefault();
    setSaving(s => ({ ...s, leaders: true }));
    try {
      const updates = [];
      for (const { key } of LEADER_ROLES) {
        const selectedId = leaders[key];
        const current = members.find(mem => mem.role === key);
        if (current && String(current.id) !== selectedId) {
          updates.push(api.put(`/members/${current.id}`, { role: 'mwanachama' }));
        }
        if (selectedId) {
          updates.push(api.put(`/members/${selectedId}`, { role: key }));
        }
      }
      await Promise.all(updates);
      const { data } = await api.get(`/members?group_id=${id}`);
      setMembers(data || []);
      showToast(T.settings.toastSavedLeaders);
    } catch {
      showToast(T.settings.toastErrLeaders, false);
    } finally {
      setSaving(s => ({ ...s, leaders: false }));
    }
  };

  const [smsTarget, setSmsTarget] = useState('all'); // 'all' | 'one'
  const [smsMemberId, setSmsMemberId] = useState('');
  const [memberSearch, setMemberSearch] = useState('');

  const filteredForSms = members.filter(m =>
    !memberSearch || m.name.toLowerCase().includes(memberSearch.toLowerCase()) || m.phone.includes(memberSearch)
  );

  const sendLoginSms = async () => {
    const isOne = smsTarget === 'one';
    if (isOne && !smsMemberId) {
      showToast(T.settings.toastErr, false);
      return;
    }
    const target = isOne
      ? members.find(m => String(m.id) === smsMemberId)?.name || 'mwanachama'
      : 'wanachama wote';
    if (!confirm(`Utumie SMS ya kuingia kwa ${target}?`)) return;
    setSaving(s => ({ ...s, sms: true }));
    try {
      const body = isOne ? { member_id: smsMemberId } : {};
      const { data } = await api.post(`/groups/${id}/send-login-sms`, body);
      showToast(data.message || T.common.success);
    } catch (err) {
      showToast(err.response?.data?.error || T.settings.toastErr, false);
    } finally {
      setSaving(s => ({ ...s, sms: false }));
    }
  };

  if (loading) return <div className="text-center py-20 text-gray-500">{T.common.loading}</div>;

  return (
    <div className="max-w-2xl space-y-6">
      {/* Toast notification */}
      {toast && (
        <div className={`fixed bottom-24 left-1/2 z-50 -translate-x-1/2 flex items-center gap-3 rounded-2xl px-5 py-3.5 text-sm font-semibold text-white shadow-[0_16px_40px_rgba(15,23,42,0.22)] transition-all ${
          toast.ok ? 'bg-green-700' : 'bg-red-600'
        }`}>
          <span className="text-lg">{toast.ok ? '✅' : '❌'}</span>
          {toast.text}
        </div>
      )}
      <div className="flex items-center gap-3">
        <Link to={`/groups/${id}`} className="text-green-700 hover:text-green-900 text-sm">← {group?.name}</Link>
        <span className="text-gray-400">/</span>
        <h1 className="text-2xl font-bold text-gray-800">{T.settings.title}</h1>
      </div>

      {/* Leaders section */}
      <Section title={`👥 ${T.settings.leaders}`}>
        <p className="text-sm text-gray-500 mb-4">
          {T.settings.leadersDesc}
        </p>
        <form onSubmit={saveLeaders} className="space-y-3">
          {LEADER_ROLES.map(({ key, label, icon }) => (
            <div key={key} className="flex items-center gap-3">
              <div className="w-36 flex items-center gap-2 shrink-0">
                <span className="text-lg">{icon}</span>
                <span className="text-sm font-medium text-gray-700">{label}</span>
              </div>
              <select
                value={leaders[key]}
                onChange={e => setLeaders(l => ({ ...l, [key]: e.target.value }))}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">{T.settings.chooseLeader}</option>
                {members.map(m => (
                  <option key={m.id} value={String(m.id)}>{m.name} ({m.phone})</option>
                ))}
              </select>
            </div>
          ))}
          <button type="submit" disabled={saving.leaders}
            className="mt-2 bg-green-700 hover:bg-green-800 text-white px-6 py-2.5 rounded-lg text-sm font-medium disabled:opacity-60">
            {saving.leaders ? T.settings.savingLeaders : T.settings.saveLeaders}
          </button>
        </form>
      </Section>

      {/* SMS section */}
      <Section title={`📱 ${T.settings.sms}`}>
        <p className="text-sm text-gray-500 mb-4">
          {T.settings.smsDesc}
        </p>

        {/* Target toggle */}
        <div className="flex rounded-xl bg-gray-100 p-1 mb-4 gap-1 max-w-xs">
          <button
            type="button"
            onClick={() => { setSmsTarget('all'); setSmsMemberId(''); setMemberSearch(''); }}
            className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-all ${
              smsTarget === 'all' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'
            }`}
          >
            👥 {T.members.allMembers}
          </button>
          <button
            type="button"
            onClick={() => setSmsTarget('one')}
            className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-all ${
              smsTarget === 'one' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'
            }`}
          >
            👤 {T.members.oneBy}
          </button>
        </div>

        {/* Single member picker */}
        {smsTarget === 'one' && (
          <div className="mb-4 space-y-2">
            <input
              type="text"
              placeholder={T.settings.searchMember}
              value={memberSearch}
              onChange={e => { setMemberSearch(e.target.value); setSmsMemberId(''); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {memberSearch && (
              <div className="max-h-52 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                {filteredForSms.length === 0 && (
                  <p className="px-4 py-3 text-sm text-gray-400">{T.settings.noMemberFound}</p>
                )}
                {filteredForSms.map(m => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => { setSmsMemberId(String(m.id)); setMemberSearch(m.name); }}
                    className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors hover:bg-blue-50 ${
                      smsMemberId === String(m.id) ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
                    }`}
                  >
                    <span>{m.name}</span>
                    <span className="text-xs text-gray-400">{m.phone}</span>
                  </button>
                ))}
              </div>
            )}
            {smsMemberId && (
              <div className="flex items-center gap-2 rounded-xl bg-blue-50 border border-blue-100 px-3 py-2 text-sm text-blue-700">
                <span>✓</span>
                <span className="font-medium">{members.find(m => String(m.id) === smsMemberId)?.name}</span>
                <button type="button" onClick={() => { setSmsMemberId(''); setMemberSearch(''); }}
                  className="ml-auto text-blue-400 hover:text-blue-700">✕</button>
              </div>
            )}
          </div>
        )}

        {/* SMS preview */}
        <div className="rounded-xl bg-blue-50 border border-blue-100 p-3 mb-4">
          <p className="text-xs text-blue-700 font-medium mb-1">{T.settings.smsPreview}</p>
          <p className="text-xs text-blue-600 italic">
            "Habari [Jina]! Msimbo wako wa kuingia M-Koba ni: <strong>123456</strong>. Halali dakika 10. Ingia hapa: http://localhost:5173"
          </p>
        </div>

        <button
          type="button"
          onClick={sendLoginSms}
          disabled={saving.sms || (smsTarget === 'one' && !smsMemberId)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
        >
          {saving.sms ? `⏳ ${T.settings.smsSending}` : smsTarget === 'all' ? `📤 ${T.settings.smsSendAll}` : `📤 ${T.settings.smsSendOne}`}
        </button>
      </Section>

      {/* Cycle settings */}
      <Section title={`⏱️ ${T.settings.cycleType}`}>
        <form onSubmit={saveCycle} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{T.settings.startDate}</label>
              <input type="date" value={cycle.start_date}
                onChange={e => setCycle({ ...cycle, start_date: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{T.settings.endDate}</label>
              <input type="date" value={cycle.end_date}
                onChange={e => setCycle({ ...cycle, end_date: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {T.settings.contributionsFrom}
              <span className="ml-1 text-xs font-normal text-gray-400">— {T.settings.contributionsFromNote}</span>
            </label>
            <input type="date" value={cycle.contributions_from}
              onChange={e => setCycle({ ...cycle, contributions_from: e.target.value })}
              className="w-full sm:w-64 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{T.settings.monthlyContribution}</label>
              <input type="number" min="0" value={cycle.monthly_contribution}
                onChange={e => setCycle({ ...cycle, monthly_contribution: e.target.value })}
                placeholder="mfano: 10000"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {T.settings.entryFee}
                <span className="ml-1 text-xs font-normal text-gray-400">— {T.settings.entryFeeNote}</span>
              </label>
              <input type="number" min="0" value={cycle.entry_fee}
                onChange={e => setCycle({ ...cycle, entry_fee: e.target.value })}
                placeholder="mfano: 20000"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {T.settings.openingBalance}
                <span className="ml-1 text-xs font-normal text-gray-400">— {T.settings.openingBalanceNote}</span>
              </label>
              <input type="number" min="0" value={cycle.opening_balance}
                onChange={e => setCycle({ ...cycle, opening_balance: e.target.value })}
                placeholder="0"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={cycle.dissolve_at_end}
                onChange={e => setCycle({ ...cycle, dissolve_at_end: e.target.checked })}
                className="w-4 h-4 text-green-600 rounded" />
              <span className="text-sm font-medium text-gray-700">
                {T.settings.dissolveAtEnd}
              </span>
            </label>

            {cycle.dissolve_at_end && (
              <div className="ml-7">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {T.settings.reserveMonths}
                  <span className="text-gray-400 font-normal ml-1">{T.settings.reserveZero}</span>
                </label>
                <div className="flex items-center gap-3">
                  <input type="number" min="0" max="12" value={cycle.reserve_months}
                    onChange={e => setCycle({ ...cycle, reserve_months: e.target.value })}
                    className="w-24 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                  <span className="text-sm text-gray-500">{T.settings.months}</span>
                </div>
                {+cycle.reserve_months > 0 && (
                  <p className="text-xs text-green-700 mt-1">
                    {T.settings.reserveMonthsNote.replace('{n}', cycle.reserve_months)}
                  </p>
                )}
              </div>
            )}
          </div>

          <button type="submit" disabled={saving.cycle}
            className="bg-green-700 hover:bg-green-800 text-white px-6 py-2.5 rounded-lg text-sm font-medium disabled:opacity-60">
            {saving.cycle ? T.settings.savingCycle : T.settings.saveCycle}
          </button>
        </form>
      </Section>

      {/* Fine rules */}
      <Section title={`⚠️ ${T.nav.fines}`}>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 mb-4">
          {T.settings.fineInfo}
        </div>
        <form onSubmit={saveFine} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {T.settings.dueDay}
              </label>
              <div className="flex items-center gap-2">
                <input type="number" min="1" max="28" value={fine.due_day}
                  onChange={e => setFine({ ...fine, due_day: e.target.value })}
                  className="w-20 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                <span className="text-sm text-gray-500">{T.settings.dueDayNote}</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {T.settings.graceDays}
              </label>
              <div className="flex items-center gap-2">
                <input type="number" min="0" value={fine.grace_days}
                  onChange={e => setFine({ ...fine, grace_days: e.target.value })}
                  className="w-20 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                <span className="text-sm text-gray-500">{T.settings.graceDaysNote}</span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Aina ya Faini</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="fine_type" value="fixed" checked={fine.fine_type === 'fixed'}
                  onChange={() => setFine({ ...fine, fine_type: 'fixed' })}
                  className="text-green-600" />
                <span className="text-sm">Kiasi Maalum (TZS)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="fine_type" value="percentage" checked={fine.fine_type === 'percentage'}
                  onChange={() => setFine({ ...fine, fine_type: 'percentage' })}
                  className="text-green-600" />
                <span className="text-sm">Asilimia (%)</span>
              </label>
            </div>
          </div>

          {fine.fine_type === 'fixed' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kiasi cha Faini (TZS)</label>
              <input type="number" min="0" value={fine.fine_amount}
                onChange={e => setFine({ ...fine, fine_amount: e.target.value })}
                className="w-40 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Asilimia ya Faini (%)</label>
              <div className="flex items-center gap-2">
                <input type="number" min="0" max="100" step="0.5" value={fine.fine_percentage}
                  onChange={e => setFine({ ...fine, fine_percentage: e.target.value })}
                  className="w-24 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                <span className="text-sm text-gray-500">% ya ada ya mwezi</span>
              </div>
            </div>
          )}

          <label className="flex items-center gap-3 cursor-pointer bg-green-50 border border-green-200 rounded-lg p-3">
            <input type="checkbox" checked={fine.is_auto}
              onChange={e => setFine({ ...fine, is_auto: e.target.checked })}
              className="w-4 h-4 text-green-600 rounded" />
            <div>
              <span className="text-sm font-medium text-gray-700">Ingiza faini kiotomatiki</span>
              <p className="text-xs text-gray-500 mt-0.5">Mfumo utaingiza faini mara moja unapofungua ukurasa wa faini</p>
            </div>
          </label>

          <button type="submit" disabled={saving.fine}
            className="bg-green-700 hover:bg-green-800 text-white px-6 py-2.5 rounded-lg text-sm font-medium disabled:opacity-60">
            {saving.fine ? T.settings.savingFine : T.settings.saveFine}
          </button>
        </form>
      </Section>

      {/* Changiwa types */}
      <Section title={`🤝 ${T.settings.changiwaTypes}`}>
        <p className="text-sm text-gray-500 mb-4">
          {T.settings.changiwaTypesDesc}
        </p>

        {/* Existing types */}
        <div className="space-y-2 mb-5">
          {changiwaTypes.length === 0 && (
            <p className="text-sm text-gray-400 py-3 text-center border border-dashed border-gray-200 rounded-lg">
              {T.settings.noChangiwaTypes}
            </p>
          )}
          {changiwaTypes.map(ct => (
            <div key={ct.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <div>
                <span className="font-medium text-gray-800">{ct.name}</span>
                {ct.description && <span className="text-xs text-gray-400 ml-2">— {ct.description}</span>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-green-700 font-semibold text-sm">
                  {new Intl.NumberFormat('sw-TZ').format(ct.amount)} TZS
                </span>
                <button
                  onClick={async () => {
                    if (!confirm(`Futa "${ct.name}"?`)) return;
                    await api.delete(`/groups/${id}/changiwa-types/${ct.id}`);
                    setChangiwaTypes(prev => prev.filter(x => x.id !== ct.id));
                  }}
                  className="text-red-400 hover:text-red-600 text-xs"
                >
                  Futa
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Add new type */}
        {addingChangiwa ? (
          <div className="border border-green-200 bg-green-50 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 mb-3">{T.settings.addNewType}</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{T.settings.typeName} *</label>
                <input type="text" placeholder={T.settings.typeNamePlaceholder}
                  value={newChangiwa.name}
                  onChange={e => setNewChangiwa({ ...newChangiwa, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{T.settings.typeAmount} *</label>
                <input type="number" min="0" placeholder={T.settings.typeAmountPlaceholder}
                  value={newChangiwa.amount}
                  onChange={e => setNewChangiwa({ ...newChangiwa, amount: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">{T.settings.typeDesc}</label>
              <input type="text" placeholder={T.settings.typeDescPlaceholder}
                value={newChangiwa.description}
                onChange={e => setNewChangiwa({ ...newChangiwa, description: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  if (!newChangiwa.name || !newChangiwa.amount) return;
                  const { data } = await api.post(`/groups/${id}/changiwa-types`, newChangiwa);
                  setChangiwaTypes(prev => [...prev, data]);
                  setNewChangiwa({ name: '', amount: '', description: '' });
                  setAddingChangiwa(false);
                }}
                className="bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-800"
              >
                {T.common.save}
              </button>
              <button
                onClick={() => { setAddingChangiwa(false); setNewChangiwa({ name: '', amount: '', description: '' }); }}
                className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50"
              >
                {T.common.cancel}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setAddingChangiwa(true)}
            className="w-full border-2 border-dashed border-green-300 text-green-700 py-3 rounded-lg text-sm font-medium hover:border-green-500 hover:bg-green-50 transition-colors"
          >
            {T.settings.addChangiwaType}
          </button>
        )}
      </Section>

      {/* Group Transfer Types */}
      <Section title={`💸 ${T.settings.transferTypes}`}>
        <p className="text-sm text-gray-500 mb-4">
          {T.settings.transferTypesDesc}
        </p>

        <div className="space-y-2 mb-5">
          {transferTypes.length === 0 && (
            <p className="text-sm text-gray-400 py-3 text-center border border-dashed border-gray-200 rounded-lg">
              {T.settings.noTransferTypes}
            </p>
          )}
          {transferTypes.map(tt => (
            <div key={tt.id} className="flex items-center justify-between bg-red-50 border border-red-100 rounded-lg px-4 py-3">
              <div>
                <span className="font-medium text-gray-800">{tt.name}</span>
                {tt.description && <span className="text-xs text-gray-400 ml-2">— {tt.description}</span>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-red-700 font-semibold text-sm">
                  {new Intl.NumberFormat('sw-TZ').format(tt.amount)} TZS
                </span>
                <button
                  onClick={async () => {
                    if (!confirm(`Futa "${tt.name}"?`)) return;
                    await api.delete(`/groups/${id}/transfer-types/${tt.id}`);
                    setTransferTypes(prev => prev.filter(x => x.id !== tt.id));
                  }}
                  className="text-red-400 hover:text-red-600 text-xs"
                >
                  {T.common.delete}
                </button>
              </div>
            </div>
          ))}
        </div>

        {addingTransferType ? (
          <div className="border border-red-200 bg-red-50 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 mb-3">{T.settings.addNewTransferType}</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{T.settings.transferTypeName} *</label>
                <input type="text" placeholder={T.settings.transferTypeNamePlaceholder}
                  value={newTransferType.name}
                  onChange={e => setNewTransferType({ ...newTransferType, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{T.settings.transferTypeAmount} *</label>
                <input type="number" min="0" placeholder={T.settings.transferTypeAmountPlaceholder}
                  value={newTransferType.amount}
                  onChange={e => setNewTransferType({ ...newTransferType, amount: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">{T.settings.transferTypeDesc}</label>
              <input type="text" placeholder={T.settings.transferTypeDescPlaceholder}
                value={newTransferType.description}
                onChange={e => setNewTransferType({ ...newTransferType, description: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" />
            </div>
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  if (!newTransferType.name || !newTransferType.amount) return;
                  const { data } = await api.post(`/groups/${id}/transfer-types`, newTransferType);
                  setTransferTypes(prev => [...prev, data]);
                  setNewTransferType({ name: '', amount: '', description: '' });
                  setAddingTransferType(false);
                  showToast(T.common.success);
                }}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700"
              >
                {T.common.save}
              </button>
              <button
                onClick={() => { setAddingTransferType(false); setNewTransferType({ name: '', amount: '', description: '' }); }}
                className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50"
              >
                {T.common.cancel}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setAddingTransferType(true)}
            className="w-full border-2 border-dashed border-red-200 text-red-600 py-3 rounded-lg text-sm font-medium hover:border-red-400 hover:bg-red-50 transition-colors"
          >
            {T.settings.addTransferType}
          </button>
        )}
      </Section>

      {/* Member permissions — superadmin only */}
    </div>
  );
}
